/**
 * Utility functions for exporting data table data to CSV/Excel formats
 */

import type { ColDef, GridApi, IRowNode } from 'ag-grid-community';
import * as XLSX from 'xlsx';

export interface ExportOptions {
	filename?: string;
	sheetName?: string;
}

/**
 * Format a raw value for export, handling various data types
 */
function formatExportValue(value: unknown): string {
	if (value == null) {
		return '';
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	if (value instanceof Date) {
		return value.toLocaleString();
	}

	return String(value);
}

/**
 * Escape a value for CSV format
 */
function escapeCSVValue(value: string): string {
	// If the value contains comma, newline, or double quote, wrap in quotes
	if (value.includes(',') || value.includes('\n') || value.includes('"')) {
		// Escape double quotes by doubling them
		return `"${value.replace(/"/g, '""')}"`;
	}
	return value;
}

/**
 * Get exportable columns (excluding action columns and hidden columns)
 */
function getExportableColumns<TData>(columns: ColDef<TData>[]): ColDef<TData>[] {
	return columns.filter(col => {
		// Skip columns without a field or valueGetter (usually action columns like delete)
		if (!col.field && !col.valueGetter && !col.colId) {
			return false;
		}

		// Skip hidden columns
		if (col.hide === true) {
			return false;
		}

		// Skip columns explicitly marked as not exportable
		if ((col as ColDef<TData> & { suppressExport?: boolean }).suppressExport) {
			return false;
		}

		return true;
	});
}

/**
 * Get the column ID used by AG Grid for a column definition
 */
function getColumnId<TData>(col: ColDef<TData>): string | undefined {
	return col.colId || col.field || undefined;
}

/**
 * Get header name for a column
 */
function getColumnHeaderName<TData>(column: ColDef<TData>): string {
	if (column.headerName) {
		return column.headerName;
	}

	// Convert field name to title case
	if (column.field) {
		return column.field
			.split('.')
			.pop()!
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, str => str.toUpperCase())
			.trim();
	}

	return '';
}

/**
 * Get the display value for a cell using AG Grid's API (respects valueGetter)
 */
function getCellValue<TData>(
	gridApi: GridApi<TData>,
	colKey: string,
	rowNode: IRowNode<TData>
): string {
	try {
		const value = gridApi.getCellValue({
			rowNode,
			colKey
		});
		return formatExportValue(value);
	} catch {
		return '';
	}
}

/**
 * Export data to CSV format
 */
export function exportToCSV<TData>(
	gridApi: GridApi<TData>,
	columns: ColDef<TData>[],
	options: ExportOptions = {}
): void {
	const { filename = 'export' } = options;

	// Get exportable columns and their IDs
	const exportColumns = getExportableColumns(columns);
	const colIds = exportColumns.map(col => getColumnId(col)).filter(Boolean) as string[];

	// Get currently displayed rows (after filtering/sorting)
	const rows: string[][] = [];
	gridApi.forEachNodeAfterFilterAndSort(node => {
		if (node.data) {
			rows.push(
				colIds.map(colId =>
					escapeCSVValue(getCellValue(gridApi, colId, node))
				)
			);
		}
	});

	// Build CSV content
	const headers = exportColumns.map(col => escapeCSVValue(getColumnHeaderName(col)));
	const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join(
		'\n'
	);

	// Create and trigger download
	downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
}

/**
 * Export data to Excel format (XLSX)
 * Uses the xlsx library for proper XLSX file generation
 */
export function exportToExcel<TData>(
	gridApi: GridApi<TData>,
	columns: ColDef<TData>[],
	options: ExportOptions = {}
): void {
	const { filename = 'export', sheetName = 'Sheet1' } = options;

	// Get exportable columns and their IDs
	const exportColumns = getExportableColumns(columns);
	const colIds = exportColumns.map(col => getColumnId(col)).filter(Boolean) as string[];

	// Build data array for xlsx
	const headers = exportColumns.map(col => getColumnHeaderName(col));
	const rows: string[][] = [];
	gridApi.forEachNodeAfterFilterAndSort(node => {
		if (node.data) {
			rows.push(
				colIds.map(colId => getCellValue(gridApi, colId, node))
			);
		}
	});

	// Create worksheet data (headers + rows)
	const wsData = [headers, ...rows];

	// Create worksheet
	const ws = XLSX.utils.aoa_to_sheet(wsData);

	// Create workbook
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, sheetName);

	// Generate and download file
	XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Helper function to trigger file download
 */
function downloadFile(
	content: string,
	filename: string,
	mimeType: string
): void {
	const blob = new Blob([content], { type: mimeType });
	const url = URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.style.display = 'none';

	document.body.appendChild(link);
	link.click();

	// Cleanup
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

/**
 * Get the count of currently displayed rows (after filtering)
 */
export function getDisplayedRowCount<TData>(gridApi: GridApi<TData>): number {
	let count = 0;
	gridApi.forEachNodeAfterFilterAndSort(() => {
		count++;
	});
	return count;
}
